import { createFileRoute, Link } from "@tanstack/react-router";
import { NotADiagnosisNote } from "@/components/safety/SafetyNotices";
import { useMemo, useState } from "react";
import { ArrowLeft, ArrowRight, Compass, LifeBuoy, RotateCcw } from "lucide-react";

import {
  THERAPY_MATCH_DISCLAIMER,
  THERAPY_MATCH_QUESTIONS,
  scoreTherapyMatch,
  type MatchOutcome,
} from "@/data/learn/therapy-match";
import { saveTherapyMatch } from "@/lib/therapy-match-results";
import { useAuthUser } from "@/hooks/useAuthUser";

export const Route = createFileRoute("/learn/therapies/assessment")({
  head: () => ({
    meta: [
      { title: "Therapy Approach Assessment — Recovery Trauma™" },
      {
        name: "description",
        content:
          "A guided, educational questionnaire that suggests trauma therapy approaches you may want to explore — with an honest note on the evidence behind each, and a clear explanation of why it appeared. Not a diagnosis.",
      },
      { property: "og:title", content: "Therapy Approach Assessment — Recovery Trauma™" },
      {
        property: "og:description",
        content:
          "Not sure where to start? Explore therapy approaches that may match what you're looking for. Educational only — never a diagnosis or a treatment recommendation.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://recoverytrauma.com/learn/therapies/assessment" },
    ],
    links: [{ rel: "canonical", href: "https://recoverytrauma.com/learn/therapies/assessment" }],
  }),
  component: TherapyAssessmentPage,
});

const EVIDENCE_LABEL = {
  established: "Established trauma treatment",
  broader: "Broader psychotherapy approach",
  emerging: "Complementary / emerging evidence",
} as const;

const EVIDENCE_STYLE = {
  established: "bg-gold/20 text-foreground",
  broader: "bg-muted text-foreground/80",
  emerging: "bg-background text-muted-foreground border border-border",
} as const;

function Disclaimer() {
  return (
    <p className="rounded-2xl border border-border/70 bg-card/50 p-4 text-xs leading-relaxed text-muted-foreground">
      {THERAPY_MATCH_DISCLAIMER}
    </p>
  );
}

function CrisisPanel() {
  return (
    <section className="rounded-2xl border border-gold/50 bg-card p-5 sm:p-6">
      <div className="flex items-center gap-2 text-foreground">
        <LifeBuoy className="h-5 w-5 text-gold" aria-hidden="true" />
        <h2 className="font-serif text-2xl">Let's pause here</h2>
      </div>
      <p className="mt-3 text-sm leading-relaxed text-foreground/80">
        You've said you're in crisis or in danger right now. Choosing a therapy approach isn't the
        right next step in this moment, so we've stopped the assessment here — that's not a failure,
        and you can come back to it any time.
      </p>
      <p className="mt-3 text-sm leading-relaxed text-foreground/80">
        What matters right now is support that can reach you today. Our Crisis Support page lists
        free helplines and services, including a finder covering more than 150 countries. If you are
        in immediate danger, contact your local emergency number.
      </p>
      <div className="mt-5 flex flex-wrap gap-3">
        <Link
          to="/crisis"
          className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-semibold text-background transition-colors hover:bg-gold/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <LifeBuoy className="h-4 w-4" aria-hidden="true" /> Crisis Support
        </Link>
        <Link
          to="/grounding"
          className="inline-flex items-center gap-2 rounded-full border border-border px-5 py-2.5 text-sm font-semibold text-foreground transition-colors hover:border-gold/60"
        >
          Grounding exercises
        </Link>
      </div>
    </section>
  );
}

function AxisBar({
  label,
  left,
  right,
  value,
}: {
  label: string;
  left: string;
  right: string;
  value: number;
}) {
  const percent = Math.round(((value + 1) / 2) * 100);
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
      <div className="relative mt-2 h-2 rounded-full bg-muted">
        <span
          className="absolute top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full bg-gold"
          style={{ left: `${percent}%` }}
          aria-hidden="true"
        />
      </div>
      <div className="mt-2 flex justify-between text-xs text-foreground/70">
        <span>{left}</span>
        <span>{right}</span>
      </div>
      <p className="sr-only">
        {label}: closer to {value < -0.15 ? left : value > 0.15 ? right : "the middle"}.
      </p>
    </div>
  );
}

function Results({
  outcome,
  onRetake,
}: {
  outcome: MatchOutcome & { answers?: Record<string, string> };
  onRetake: () => void;
}) {
  const user = useAuthUser();
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [saveError, setSaveError] = useState("");

  if (outcome.risk) return <CrisisPanel />;

  if (outcome.results.length === 0) {
    return (
      <div className="space-y-6">
        <h2 className="font-serif text-3xl text-foreground">Nothing stood out strongly</h2>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Your answers didn't point clearly towards particular approaches — that's common, and it
          isn't a problem. You may find it more useful to browse the approaches directly and see
          what reads as though it fits.
        </p>
        <div className="flex flex-wrap gap-3">
          <Link
            to="/learn/therapies"
            className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-semibold text-background transition-colors hover:bg-gold/90"
          >
            Browse all approaches
          </Link>
          <button
            type="button"
            onClick={onRetake}
            className="inline-flex items-center gap-2 rounded-full border border-border px-5 py-2.5 text-sm font-semibold text-foreground transition-colors hover:border-gold/60"
          >
            <RotateCcw className="h-4 w-4" aria-hidden="true" /> Retake the assessment
          </button>
        </div>
        <Disclaimer />
      </div>
    );
  }

  async function handleSave() {
    setSaveState("saving");
    const res = await saveTherapyMatch({
      answers: outcome.answers ?? {},
      approaches: outcome.results.map((r) => ({
        slug: r.profile.slug,
        name: r.profile.name,
        label: r.label,
      })),
    });
    if (res.ok) {
      setSaveState("saved");
    } else {
      setSaveError(res.error ?? "We couldn't save your results just now.");
      setSaveState("error");
    }
  }

  return (
    <div className="space-y-6">
      <header>
        <h2 className="font-serif text-3xl text-foreground">Approaches you may want to explore</h2>
        <NotADiagnosisNote className="mt-3" />
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
          These are starting points for reading and for conversations — not a recommendation, and
          not a ranking of what will work for you. Approaches differ in how much research supports
          them for trauma, so each one below says where it stands.
        </p>
      </header>

      <ol className="space-y-4">
        {outcome.results.map((result) => (
          <li
            key={result.profile.slug}
            className="rounded-2xl border border-border/70 bg-card/70 p-5 shadow-sm"
          >
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-gold/20 px-3 py-1 text-xs font-semibold text-foreground">
                {result.label}
              </span>
              <span
                className={`rounded-full px-2 py-0.5 text-[10px] ${EVIDENCE_STYLE[result.profile.evidence]}`}
              >
                {EVIDENCE_LABEL[result.profile.evidence]}
              </span>
            </div>
            <h3 className="mt-3 font-serif text-2xl text-foreground">
              {result.profile.abbrev && result.profile.abbrev !== result.profile.name
                ? `${result.profile.abbrev} — ${result.profile.name}`
                : result.profile.name}
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-foreground/80">
              <span className="font-semibold text-foreground">What it involves: </span>
              {result.profile.involves}
            </p>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              <span className="font-semibold text-foreground/80">The evidence: </span>
              {result.profile.evidenceNote}
            </p>
            <div className="mt-4 rounded-xl border border-border/60 bg-background/40 p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Why this appeared in your results
              </p>
              <ul className="mt-2 list-disc space-y-1 ps-5 text-sm leading-relaxed text-foreground/80">
                {result.reasons.map((reason) => (
                  <li key={reason}>Because {reason}.</li>
                ))}
              </ul>
            </div>
            <Link
              to={result.profile.to}
              className="mt-4 inline-block text-sm font-semibold text-foreground underline-offset-4 hover:underline"
            >
              Read our full {result.profile.abbrev ?? result.profile.name} guide →
            </Link>
          </li>
        ))}
      </ol>

      <section className="rounded-2xl border border-border/70 bg-card/50 p-5">
        <h3 className="font-serif text-xl text-foreground">Your preferences</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          A summary of how you said you'd like to work — useful to bring to a first conversation
          with a therapist.
        </p>
        <div className="mt-4 grid gap-5 sm:grid-cols-3">
          <AxisBar
            label="Way of working"
            left="Structured"
            right="Exploratory"
            value={outcome.axes.structureExplore}
          />
          <AxisBar
            label="Focus"
            left="Cognitive"
            right="Body-focused"
            value={outcome.axes.cognitiveBody}
          />
          <AxisBar
            label="Time frame"
            left="Present-focused"
            right="Past-focused"
            value={outcome.axes.presentPast}
          />
        </div>
      </section>

      <section className="rounded-2xl border border-border/70 bg-card/50 p-5">
        <h3 className="font-serif text-xl text-foreground">Keep this</h3>
        {user ? (
          <>
            <p className="mt-1 text-sm text-muted-foreground">
              Save these results to your account so you can look back at them, and retake the
              assessment whenever things change.
            </p>
            <div className="mt-4 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={handleSave}
                disabled={saveState === "saving" || saveState === "saved"}
                className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-semibold text-background transition-colors hover:bg-gold/90 disabled:opacity-60"
              >
                {saveState === "saved"
                  ? "Saved to your account"
                  : saveState === "saving"
                    ? "Saving…"
                    : "Save my results"}
              </button>
              <Link
                to="/dashboard"
                className="inline-flex items-center gap-2 rounded-full border border-border px-5 py-2.5 text-sm font-semibold text-foreground transition-colors hover:border-gold/60"
              >
                Go to my dashboard
              </Link>
            </div>
            {saveState === "error" ? (
              <p className="mt-3 text-sm text-foreground/80">{saveError}</p>
            ) : null}
          </>
        ) : (
          <p className="mt-1 text-sm text-muted-foreground">
            <Link to="/login" className="font-semibold text-foreground underline underline-offset-4">
              Sign in
            </Link>{" "}
            to save your results to your account and return to them later.
          </p>
        )}
      </section>

      <div className="flex flex-wrap gap-3">
        <button
          type="button"
          onClick={onRetake}
          className="inline-flex items-center gap-2 rounded-full border border-border px-5 py-2.5 text-sm font-semibold text-foreground transition-colors hover:border-gold/60"
        >
          <RotateCcw className="h-4 w-4" aria-hidden="true" /> Retake the assessment
        </button>
        <Link
          to="/learn/therapies"
          className="inline-flex items-center gap-2 rounded-full border border-border px-5 py-2.5 text-sm font-semibold text-foreground transition-colors hover:border-gold/60"
        >
          Browse all approaches
        </Link>
      </div>

      <Disclaimer />
    </div>
  );
}

function TherapyAssessmentPage() {
  const [started, setStarted] = useState(false);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [finished, setFinished] = useState(false);

  const total = THERAPY_MATCH_QUESTIONS.length;
  const question = THERAPY_MATCH_QUESTIONS[step];
  const chosen = question ? answers[question.id] : undefined;
  const riskStop = answers["safety"] === "yes";

  const outcome = useMemo<MatchOutcome & { answers?: Record<string, string> }>(() => {
    const o = scoreTherapyMatch(answers);
    return { ...o, answers };
  }, [answers]);

  function choose(value: string) {
    if (!question) return;
    const next = { ...answers, [question.id]: value };
    setAnswers(next);
    const option = question.options.find((o) => o.value === value);
    if (option?.risk) {
      setFinished(true);
      return;
    }
    if (step + 1 >= total) {
      setFinished(true);
    } else {
      setStep(step + 1);
    }
  }

  function restart() {
    setAnswers({});
    setStep(0);
    setFinished(false);
    setStarted(true);
  }

  const progress = Math.round(((step + (chosen ? 1 : 0)) / total) * 100);

  return (
    <main className="mx-auto max-w-3xl px-4 py-10">
      <nav aria-label="Breadcrumb" className="mb-4 text-sm text-muted-foreground">
        <Link to="/learn" className="hover:text-foreground hover:underline">
          Learn
        </Link>
        <span aria-hidden="true"> / </span>
        <Link to="/learn/therapies" className="hover:text-foreground hover:underline">
          Therapies &amp; Healing Approaches
        </Link>
        <span aria-hidden="true"> / </span>
        <span className="text-foreground">Therapy Approach Assessment</span>
      </nav>

      {!started && !finished ? (
        <div className="space-y-6">
          <header>
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs text-muted-foreground">
              <Compass className="h-3.5 w-3.5 text-gold" aria-hidden="true" /> Guided assessment
            </div>
            <h1 className="font-serif text-4xl tracking-tight text-foreground md:text-5xl">
              Find a therapy approach that may fit you
            </h1>
            <p className="mt-4 text-base leading-relaxed text-muted-foreground">
              Around {total} short questions, one at a time, about what you'd like support with and
              how you prefer to work. At the end you'll see three approaches worth reading about,
              each with an honest note on the evidence behind it and a plain explanation of why it
              appeared.
            </p>
          </header>
          <ul className="space-y-2 text-sm leading-relaxed text-foreground/80">
            <li>• No scores, no percentages and nothing that diagnoses you.</li>
            <li>• You can stop at any point, and retake it whenever things change.</li>
            <li>• Members can save results to their account.</li>
          </ul>
          <button
            type="button"
            onClick={() => setStarted(true)}
            className="inline-flex items-center gap-2 rounded-full bg-gold px-6 py-3 text-sm font-semibold text-background transition-colors hover:bg-gold/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            Start assessment <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </button>
          <Disclaimer />
        </div>
      ) : null}

      {started && !finished && question ? (
        <div className="space-y-6">
          <div>
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>
                Question {step + 1} of {total}
              </span>
              <span>{question.section}</span>
            </div>
            <div
              className="mt-2 h-2 w-full overflow-hidden rounded-full bg-muted"
              role="progressbar"
              aria-valuenow={step + 1}
              aria-valuemin={1}
              aria-valuemax={total}
              aria-label="Assessment progress"
            >
              <div
                className="h-full rounded-full bg-gold transition-all duration-300"
                style={{ width: `${Math.max(progress, 4)}%` }}
              />
            </div>
          </div>

          <section className="rounded-2xl border border-border/70 bg-card/70 p-5 sm:p-6">
            <h1 className="font-serif text-2xl leading-snug text-foreground sm:text-3xl">
              {question.text}
            </h1>
            {question.help ? (
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{question.help}</p>
            ) : null}
            <ul className="mt-5 space-y-2">
              {question.options.map((option) => {
                const active = chosen === option.value;
                return (
                  <li key={option.value}>
                    <button
                      type="button"
                      onClick={() => choose(option.value)}
                      aria-pressed={active}
                      className="rt-choice w-full rounded-xl px-4 py-3 text-start text-sm leading-relaxed focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      {option.label}
                    </button>
                  </li>
                );
              })}
            </ul>
          </section>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <button
              type="button"
              onClick={() => (step === 0 ? setStarted(false) : setStep(step - 1))}
              className="inline-flex items-center gap-2 rounded-full border border-border px-5 py-2.5 text-sm font-semibold text-foreground transition-colors hover:border-gold/60"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Back
            </button>
            <button
              type="button"
              onClick={() => (step + 1 >= total ? setFinished(true) : setStep(step + 1))}
              disabled={!chosen}
              className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-semibold text-background transition-colors hover:bg-gold/90 disabled:opacity-50"
            >
              {step + 1 >= total ? "See approaches" : "Next"}{" "}
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>

          {riskStop ? null : <Disclaimer />}
        </div>
      ) : null}

      {finished ? <Results outcome={outcome} onRetake={restart} /> : null}
    </main>
  );
}
